import { NotificationType } from '@kcftech/prometheus-client';
import { DocumentTitle } from 'components/DocumentTitle';
import { Detail } from 'components/detail/detail';
import {
    DetailsColumns,
    DetailsContainer,
    DetailsSection,
} from 'components/detail/styled';
import { NoValue } from 'components/no_value/NoValue';
import { NotificationSubscriptionHierarchy } from 'components/notification_subscriptions/NotificationSubscriptionHierarchy';
import { DetailsPage } from 'components/page/DetailsPage';
import {
    NotificationSubscriptionsFilter,
    usePagedSingleTypeNotificationSubscriptionsDataSource,
} from 'custom_hooks/data_hooks/data_source/notificationSubscriptionsDataSource';
import { NotificationSubscriptionsGroupDetailsTable } from 'pages/Settings/NotificationSubscriptions/NotificationSubscriptionsGroupDetailsTable';
import {
    getNotificationTypeDisplayName,
    getSubscriptionGroupId,
} from 'pages/Settings/NotificationSubscriptions/shared';
import { useEffect, useMemo } from 'react';

export type IndicatorAlertsGroupedSubscriptionsDetailsPageProps = Readonly<{
    groupId?: string;
    locationId?: string;
    notificationType: NotificationType;
}>;

export function GroupedSubscriptionsDetailsPage({
    groupId,
    locationId,
    notificationType,
}: IndicatorAlertsGroupedSubscriptionsDetailsPageProps) {
    const defaultFilter = useMemo<NotificationSubscriptionsFilter>(
        () => ({
            groupId,
            locationId,
            isSubscriptionGroupMode: true,
            notificationType,
        }),
        [groupId, locationId, notificationType],
    );

    const dataSource = usePagedSingleTypeNotificationSubscriptionsDataSource(
        notificationType,
        {
            name: 'notification-subscriptions-details-table',
            defaultFilter,
        },
    );

    const { error, isLoading, results, paging, setFilter } = dataSource;

    // Each subscription on this page will have the same corporation / location / group name,
    // so we pick the first one to display in the top section
    const optionalFirstResult = results?.[0];
    const corporationName = optionalFirstResult?.Corporation.CorporationName;
    const locationName = optionalFirstResult?.Location.LocationName;
    const groupName =
        optionalFirstResult && 'Group' in optionalFirstResult
            ? optionalFirstResult.Group?.GroupName
            : undefined;
    const typeDisplayName = optionalFirstResult
        ? getNotificationTypeDisplayName(optionalFirstResult)
        : '';

    useEffect(() => {
        setFilter({
            groupId,
            locationId,
            isSubscriptionGroupMode: true,
            notificationType,
        });
    }, [groupId, locationId, notificationType, setFilter]);

    return (
        <DetailsPage
            error={error ? 'Unable to load notification subscriptions' : undefined}
            invalidURL={!results && !isLoading && !!error}
            isLoading={isLoading}
            fullHeight
            fullWidth
            noPageScroll
        >
            <DocumentTitle
                title={`${groupName ?? locationName} Notification Subscriptions`}
            />
            {results && (
                <>
                    <DetailsContainer>
                        <DetailsColumns className="mt-20">
                            <DetailsSection>
                                <Detail name="Corporation">{corporationName}</Detail>
                                <Detail name="Location">{locationName}</Detail>
                            </DetailsSection>
                            <DetailsSection>
                                <Detail name="Type">{typeDisplayName}</Detail>
                                <Detail name="Total Subscriptions">
                                    {paging.totalResults}
                                </Detail>
                            </DetailsSection>
                            <DetailsSection>
                                <Detail name="Hierarchy">
                                    {optionalFirstResult &&
                                        (getSubscriptionGroupId(optionalFirstResult) ? (
                                            <NotificationSubscriptionHierarchy
                                                subscription={optionalFirstResult}
                                                showLocation={false}
                                                showCorporation={false}
                                            />
                                        ) : (
                                            <NoValue />
                                        ))}
                                </Detail>
                            </DetailsSection>
                        </DetailsColumns>
                    </DetailsContainer>

                    <DetailsContainer className="overflow-hidden">
                        <NotificationSubscriptionsGroupDetailsTable
                            dataSource={dataSource}
                            notificationType={notificationType}
                        />
                    </DetailsContainer>
                </>
            )}
        </DetailsPage>
    );
}
